import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError, decideApplication, editApplicationAnswers, excludeApplication,
  getApplicationComments, getTeamApplications, getTeams, importApplications,
  postApplicationComment, putTeamForm, removeApplicationComment,
  type ApplicationAnswers, type ApplicationFilter, type ApplicationFormConfig,
  type ApplicationImport, type IntakeQuestion, type TeacherApplicationRow, type TeamsOverview,
} from "../api";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useShell } from "../Layout";
import { QuestionEditor } from "../QuestionEditor";
import {
  APPLICANT_TABS, isApplicantTab, routes, type ApplicantTab,
} from "../routes";
import * as csv from "../csv";
import { tracked, useAsync } from "../useAsync";
import { Badge, Card, CardBody, CardHead, DASH, Empty, Freshness, Pane, When } from "../ui";
import { ApplicationQuestions, answersFor } from "./Apply";
import { LinkTrio, SetField } from "./Projects";

/**
 * A cohort's intake: the questions, the settings, and everybody who applied — built like Teams
 * (owner, 2026-10-13: *"for the aplicants, can we have a similiar system as teams!"*).
 *
 * 🔴 ONE CAPABILITY FOR EVERY WRITE HERE — Applicants & teams (`can_manage_teams`): the form,
 * the answers, setting somebody aside, accepting or declining, a comment, loading a file. A
 * control you lack is disabled where it sits and says why, because one that is simply absent
 * reads as a feature that does not exist.
 *
 * 🔴 A DECISION AND A COMMENT ARE FOR TEACHERS. The applicant's page says nothing of either
 * until a later Publish decisions step (owner: *"later on publish"*), and accepting invites
 * nobody into Classroom (*"not now"*) — so the page says both where they are pressed.
 *
 * ⚠️ THE CODE STAYS `getTeams` / `TeamsOverview`. The types are a contract across two
 * repositories and renaming them buys nothing a teacher would see.
 */
export function ApplicantsPage() {
  const { courseId, access } = useShell();
  const { tab: rawTab } = useParams();
  const overview = useAsync(() => getTeams(courseId), [courseId]);
  const loaded = overview.data;
  // ⚠️ ONE DRAFT AND ONE SAVE FOR TWO TABS, the Teams page's rule: two saves on one endpoint is
  // how whichever was pressed last silently undid the other.
  const [qDraft, setQDraft] = useState<IntakeQuestion[] | null>(null);
  const [fDraft, setFDraft] = useState<ApplicationFormConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyOpen, setBusyOpen] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [wrote, setWrote] = useState<string[]>([]);
  const canEdit = access.can_manage_teams;

  // 🔴 `form` WAS A TAB AND IS NOW TWO. That address is in bookmarks, so it redirects rather
  // than falling through to a default with nothing saying so.
  if (rawTab === "form") {
    return <Navigate to={routes.applicants(courseId, "questions")} replace />;
  }
  const tab: ApplicantTab = isApplicantTab(rawTab ?? "")
    ? (rawTab as ApplicantTab) : "applications";

  const questions = qDraft ?? loaded?.questions ?? [];
  const form = fDraft ?? loaded?.form ?? null;
  const changes = loaded && form ? intakeChanges(loaded, form, questions)
    : { questions: 0, settings: 0, total: 0 };
  const dirty = changes.total > 0;

  const save = async () => {
    if (!loaded || !form) return;
    setBusy(true); setErr(null); setWrote([]);
    try {
      // ⚠️ `enabled` COMES FROM THE SERVER, NOT THE DRAFT: the switch writes at once and is
      // never part of this save, so a stale copy here cannot re-close a form just opened.
      const r = await putTeamForm(courseId, { ...form, enabled: loaded.form.enabled,
                                              questions });
      setQDraft(null); setFDraft(null);
      setWrote(r.changed ?? []);
      overview.reload();
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };

  // 🔴 NOT PART OF THE DRAFT: this is the one control that changes what applicants see the
  // moment it is pressed, and it sends no questions, so it changes none.
  const toggleOpen = () => {
    if (!loaded) return;
    setBusyOpen(true); setErr(null);
    tracked(putTeamForm(courseId, { ...loaded.form, enabled: !loaded.form.enabled }))
      .then(() => overview.reload())
      .catch((e) => setErr(e as ApiError))
      .finally(() => setBusyOpen(false));
  };

  const href = (x: ApplicantTab, show = "") =>
    routes.applicants(courseId, x) + (show ? `?show=${show}` : "");
  const LABEL: Record<ApplicantTab, string> = {
    questions: "Questions", settings: "Settings", applications: "Applications",
  };
  const DIRTY: Partial<Record<ApplicantTab, number>> = {
    questions: changes.questions, settings: changes.settings,
  };

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Cohort</p>
        <h2>Applicants</h2>
        {loaded && <span className="cohort-tag">{loaded.course.name}</span>}
        <span className="fresh"><Freshness q={overview} /></span>
      </div>

      <Pane q={overview} empty={<Empty title="Nothing to show for this cohort yet" />}>
        {(d) => (
          <div className="stack">
            {/* 🔴 THE TEAMS STRIP. Every number links to the list it counts, and each is the
                server's own count from the definition that files the rows under the filters —
                so Accepted here is the length of the Accepted list. */}
            <div className="glance">
              <Link to={href("settings")}><span className="k">Closes</span>
                <span className="v">
                  {d.form.closes_at ? <When iso={d.form.closes_at} />
                    : <span className="muted">No deadline</span>}
                </span></Link>
              <Link to={href("applications")}><span className="k">Applied</span>
                <span className="v num">{d.counts.applications}</span></Link>
              <Link to={href("applications", "accepted")}><span className="k">Accepted</span>
                <span className="v num">{d.counts.accepted}
                  <span className="muted"> of {d.counts.applications}</span></span></Link>
              <Link to={href("applications", "enrolled")}><span className="k">Enrolled</span>
                <span className="v num">{d.counts.enrolled}
                  <span className="muted"> of {d.counts.accepted} accepted</span></span></Link>
              <div className="glance-open">
                <span className="k">Open to applicants</span>
                <span className={d.state === "open" ? "statepill on" : "statepill"}>
                  <span className="statedot" />{d.state}
                </span>
                <button className="btn sm" disabled={!canEdit || busyOpen}
                        onClick={toggleOpen}>
                  {busyOpen ? "…" : d.state === "open" ? "Close it" : "Open it"}
                </button>
                <LinkTrio open={d.state === "open"} who="applicant"
                          url={`${window.location.origin}/apply/${encodeURIComponent(courseId)}`}
                          preview={routes.applicantsPreview(courseId)}
                          openTitle="Open the link applicants use. It shows you the form as anybody signed in with your account would see it." />
              </div>
            </div>

            <div className="ptabs" role="tablist">
              {APPLICANT_TABS.map((x) => (
                <Link key={x} className="ptab" role="tab" aria-selected={tab === x}
                      to={href(x)}>
                  {LABEL[x]}
                  {x === "questions" && <span className="nav-count">{questions.length}</span>}
                  {x === "applications" && d.counts.applications > 0 && (
                    <span className="nav-count">{d.counts.applications}</span>)}
                  {(DIRTY[x] ?? 0) > 0 && (
                    <span className="unsaved" aria-label="unsaved changes" role="img" />)}
                </Link>))}
            </div>

            {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
            {wrote.length > 0 && !dirty && (
              <p className="alert info" role="status">Saved: {wrote.join(", ")}</p>)}
            {!canEdit && tab !== "applications" && (
              <p className="small muted">
                You can read this but not change it — that needs <b>Applicants &amp; teams</b>
                {" "}on this cohort.
              </p>)}

            {tab === "questions" && (
              <Card>
                <CardHead title="The questions"
                          desc={<>What this cohort asks anybody who applies. A new form starts
                                  as the programme&apos;s twelve; add, reword, reorder or remove
                                  them.</>} />
                <CardBody>
                  <QuestionEditor questions={questions} shape="intake" disabled={!canEdit}
                                  onChange={setQDraft} />
                </CardBody>
              </Card>)}

            {tab === "settings" && form && (
              <SettingsTab form={form} loaded={d.form} canEdit={canEdit}
                           onChange={setFDraft} />)}

            {tab === "applications" && (
              <ApplicationsTab d={d} courseId={courseId} canEdit={canEdit}
                               onChanged={overview.reload} />)}

            {dirty && canEdit && (
              <div className="savebar" role="status">
                <span><b>{changes.total} unsaved change{changes.total === 1 ? "" : "s"}</b>{" "}
                  <span className="muted">
                    · {[changes.questions ? `${changes.questions} in Questions` : "",
                        changes.settings ? `${changes.settings} in Settings` : ""]
                       .filter(Boolean).join(" and ")}. One save writes the form and its
                    questions together.
                  </span>
                </span>
                <span className="spacer" />
                <button className="btn ghost" disabled={busy}
                        onClick={() => { setQDraft(null); setFDraft(null); }}>Discard</button>
                <button className="btn primary" disabled={busy} onClick={() => void save()}>
                  {busy ? "Saving…"
                    : `Save ${changes.total} change${changes.total === 1 ? "" : "s"}`}
                </button>
              </div>)}
          </div>
        )}
      </Pane>
    </div>
  );
}

/** What changed, and on which tab. Derived, never stored. */
export function intakeChanges(loaded: TeamsOverview, form: ApplicationFormConfig,
                              questions: IntakeQuestion[]):
    { questions: number; settings: number; total: number } {
  const SETTINGS = ["intro", "closes_at", "max_applications"] as const;
  let settings = 0;
  for (const k of SETTINGS) if (loaded.form[k] !== form[k]) settings += 1;
  let changed = 0;
  const n = Math.max(loaded.questions.length, questions.length);
  for (let i = 0; i < n; i += 1) {
    if (JSON.stringify(loaded.questions[i]) !== JSON.stringify(questions[i])) changed += 1;
  }
  return { questions: changed, settings, total: changed + settings };
}

/** How the round runs. Saved together with the questions — laid out as the Teams settings are. */
function SettingsTab({ form, loaded, canEdit, onChange }: {
  form: ApplicationFormConfig; loaded: ApplicationFormConfig; canEdit: boolean;
  onChange: (f: ApplicationFormConfig) => void;
}) {
  const set = <K extends keyof ApplicationFormConfig>(k: K, v: ApplicationFormConfig[K]) =>
    onChange({ ...form, [k]: v });
  return (
    <Card>
      <CardHead title="Settings" desc="How the round runs. Saved together with the questions." />
      <CardBody>
        <div className="stack">
          <SetField label="Introduction" hint="Shown above the questions. Markdown."
                    edited={form.intro !== loaded.intro}>
            <textarea className="ta prose" rows={4} value={form.intro} maxLength={4000}
                      disabled={!canEdit} aria-label="Introduction"
                      onChange={(e) => set("intro", e.target.value)} />
          </SetField>
          <SetField label="Closes"
                    hint="ISO 8601, or blank for no deadline. UTC unless you say otherwise; applicants see it in their own time zone."
                    edited={form.closes_at !== loaded.closes_at}>
            <input className="input wide" value={form.closes_at} disabled={!canEdit}
                   placeholder="2026-10-31T23:59:00Z" aria-label="Closes"
                   onChange={(e) => set("closes_at", e.target.value)} />
          </SetField>
          <SetField label="Most applications"
                    hint="The form refuses new applications past this number. 0 means none are accepted."
                    edited={form.max_applications !== loaded.max_applications}>
            <input className="input" type="number" min={0} max={2000} disabled={!canEdit}
                   value={form.max_applications} aria-label="Most applications"
                   onChange={(e) => set("max_applications", Number(e.target.value))} />
          </SetField>
        </div>
      </CardBody>
    </Card>
  );
}

// ── applications ───────────────────────────────────────────────────────────────────

const FILTERS: { key: "" | ApplicationFilter; label: string }[] = [
  { key: "", label: "All" },
  { key: "waiting", label: "Waiting for a decision" },
  { key: "accepted", label: "Accepted" },
  { key: "enrolled", label: "Enrolled" },
  { key: "declined", label: "Declined" },
  { key: "aside", label: "Set aside" },
  { key: "withdrawn", label: "Withdrawn" },
];

const BADGE: Record<ApplicationFilter, { tone: "ok" | "warn" | "stop" | "plain"; word: string }> = {
  waiting: { tone: "warn", word: "waiting" },
  accepted: { tone: "ok", word: "accepted" },
  enrolled: { tone: "ok", word: "enrolled" },
  declined: { tone: "stop", word: "declined" },
  aside: { tone: "plain", word: "set aside" },
  withdrawn: { tone: "plain", word: "withdrawn" },
};

/** An answer as one line of text, for the search box and the spreadsheet. */
export function answerText(q: IntakeQuestion, v: ApplicationAnswers[string] | undefined):
    string {
  if (v === undefined || v === "") return "";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  return (q.rows ?? Object.keys(v)).filter((r) => v[r]).map((r) => `${r}=${v[r]}`).join("; ");
}

/**
 * The applications as a spreadsheet.
 *
 * ⚠️ A GRID IS ONE COLUMN PER ROW, `Label [Row]` — the way Google Forms writes it, and what the
 * import reads back — so the file loads into another cohort unchanged.
 * 🔴 NO COMMENT AND NO NOTE: a file leaves the console, and what teachers write about somebody
 * stays here.
 */
export function applicationsSheet(rows: TeacherApplicationRow[], questions: IntakeQuestion[]):
    { header: string[]; body: string[][] } {
  const cols: { label: string; read: (r: TeacherApplicationRow) => string }[] = [];
  for (const q of questions) {
    if (q.kind === "matrix") {
      for (const row of q.rows ?? []) {
        cols.push({ label: `${q.label} [${row}]`, read: (r) => {
          const v = r.answers[q.id];
          return v && typeof v === "object" ? v[row] ?? "" : "";
        } });
      }
    } else {
      cols.push({ label: q.label, read: (r) => answerText(q, r.answers[q.id]) });
    }
  }
  return {
    header: ["Email", "Applied", "State", "Decision", ...cols.map((c) => c.label)],
    body: rows.map((r) => [r.applicant.email, r.submitted_at, r.state, r.decision,
                           ...cols.map((c) => c.read(r))]),
  };
}

function ApplicationsTab({ d, courseId, canEdit, onChanged }: {
  d: TeamsOverview; courseId: string; canEdit: boolean; onChanged: () => void;
}) {
  const q = useAsync(() => getTeamApplications(courseId), [courseId]);
  const [params, setParams] = useSearchParams();
  const show = (params.get("show") ?? "") as "" | ApplicationFilter;
  const chosen = params.get("a") ?? "";
  const [find, setFind] = useState("");
  const [imp, setImp] = useState<{ text: string; name: string; plan: ApplicationImport } | null>(
    null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [copied, setCopied] = useState<{ n: number; text: string; failed: boolean } | null>(
    null);

  const setParam = useCallback((k: string, v: string) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (v) next.set(k, v); else next.delete(k);
      return next;
    }, { replace: k === "a" });
  }, [setParams]);

  const questions = q.data?.questions ?? d.questions;
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const needle = find.trim().toLowerCase();
  const shown = rows.filter((r) => {
    if (show && !r.in.includes(show)) return false;
    if (!needle) return true;
    const hay = [r.applicant.display_name, r.applicant.email, r.full_name,
                 ...questions.map((x) => answerText(x, r.answers[x.id]))].join(" ").toLowerCase();
    return hay.includes(needle);
  });
  const count = (k: "" | ApplicationFilter) => (k ? rows.filter((r) => r.in.includes(k)).length
    : rows.length);
  // ⚠️ THE CHOSEN APPLICANT IS IN THE ADDRESS (`?a=`), so a link opens straight onto them; with
  // none chosen the first in the list is shown — on a wide screen only, where it costs nothing.
  const current = rows.find((r) => r.id === chosen) ?? shown[0];
  const invite = rows.filter((r) => r.decision === "accepted" && !r.on_roster
                                    && !r.withdrawn_at);

  const preview = async (f: File) => {
    setBusy("import"); setErr(null);
    try {
      const text = await f.text();
      setImp({ text, name: f.name, plan: await importApplications(courseId, text) });
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(""); }
  };
  const load = async () => {
    if (!imp) return;
    setBusy("import"); setErr(null);
    try {
      await importApplications(courseId, imp.text, true);
      setImp(null); q.reload(); onChanged();
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(""); }
  };
  const exportJson = () => {
    const doc = {
      kind: "turbine-applications", version: 1, cohort: d.course.name,
      exported_at: new Date().toISOString(), questions,
      rows: shown.map((r) => ({ email: r.applicant.email, submitted_at: r.submitted_at,
                                answers: r.answers })),
    };
    const blob = new Blob([JSON.stringify(doc, null, 2)],
                          { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = csv.stamped(d.course.name, "applications").replace(/\.csv$/, ".json");
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const copyInvite = () => {
    const text = invite.map((r) => r.applicant.email).join("\n");
    // ⚠️ IN THE CLICK, AND A REFUSAL SHOWS THE ADDRESSES TO COPY BY HAND rather than a tick
    // for a copy that did not happen.
    (navigator.clipboard?.writeText(text) ?? Promise.reject(new Error("no clipboard")))
      .then(() => setCopied({ n: invite.length, text, failed: false }))
      .catch(() => setCopied({ n: invite.length, text, failed: true }));
  };
  const changed = () => { q.reload(); onChanged(); };

  const csvLabel = `Download ${shown.length} application${shown.length === 1 ? "" : "s"} as a spreadsheet (CSV)`;
  const jsonLabel = "Download these applications for another console (JSON)";
  const loadLabel = "Load applications from a CSV or JSON file";

  return (
    <div className="ap-wrap">
      <div className="log-filters ap-filters">
        {FILTERS.filter((f) => f.key !== "withdrawn" || count("withdrawn") > 0).map((f) => (
          <button key={f.key || "all"} className={show === f.key ? "chip on" : "chip"}
                  aria-pressed={show === f.key} onClick={() => setParam("show", f.key)}>
            {f.label}<span className="cnt">{count(f.key)}</span>
          </button>))}
        <input className="input grow" value={find} placeholder="Find a name, an email or an answer"
               aria-label="Find a name, an email or an answer"
               onChange={(e) => setFind(e.target.value)} />
        <button className="btn sm" disabled={invite.length === 0} onClick={copyInvite}
                title={`Copy the ${invite.length} accepted applicants who are not on the Classroom roster yet, one email a line, to invite them in Classroom`}>
          Copy {invite.length} to invite
        </button>
        <button className="btn sm icon" disabled={shown.length === 0} title={csvLabel}
                aria-label={csvLabel}
                onClick={() => {
                  const { header, body } = applicationsSheet(shown, questions);
                  csv.download(csv.stamped(d.course.name, "applications"),
                               csv.toCsv(header, body));
                }}>
          <span aria-hidden="true">⤓</span>
        </button>
        <button className="btn sm icon" disabled={shown.length === 0} title={jsonLabel}
                aria-label={jsonLabel} onClick={exportJson}>
          <span aria-hidden="true">{"{ }"}</span>
        </button>
        {canEdit && (
          <label className="btn sm icon" title={loadLabel}>
            <span aria-hidden="true">⤒</span>
            <span className="sr">{loadLabel}</span>
            <input type="file" accept=".csv,.json,text/csv,application/json" className="sr"
                   disabled={busy === "import"}
                   onChange={(e) => {
                     const f = e.target.files?.[0];
                     e.target.value = "";
                     if (f) void preview(f);
                   }} />
          </label>)}
      </div>

      {!canEdit && (
        <p className="small muted">
          Accepting, declining, commenting, editing and loading a file need <b>Applicants
          &amp; teams</b> on this cohort.
        </p>)}
      {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
      {copied && !copied.failed && (
        <p className="alert info" role="status">
          Copied {copied.n} address{copied.n === 1 ? "" : "es"}. Paste them into Classroom →
          People → Invite students. Nobody is invited from here.
        </p>)}
      {copied?.failed && (
        <div className="alert warn stack" role="status">
          <span>This browser would not copy. The {copied.n} address{copied.n === 1 ? "" : "es"}
            {" "}to invite in Classroom:</span>
          <textarea className="ta" readOnly rows={Math.min(copied.n, 8)} value={copied.text}
                    aria-label="Addresses to invite"
                    onFocus={(e) => e.currentTarget.select()} />
        </div>)}
      {imp && <ImportPreview name={imp.name} plan={imp.plan} busy={busy === "import"}
                             onCancel={() => setImp(null)} onLoad={() => void load()} />}

      <Pane q={q}>
        {() => rows.length === 0
          ? <Card><CardBody><Empty title="Nobody has applied yet"
                                   hint="Open the form and share the link." /></CardBody></Card>
          : (
            <div className={chosen ? "ap-split ap-open" : "ap-split"}>
              <div className="card ap-list-card">
                {shown.length === 0
                  ? <p className="ap-empty">Nothing matches that.</p>
                  : (
                    <ol className="ap-list" aria-label="Applications">
                      {shown.map((r) => (
                        <li key={r.id}>
                          <button type="button"
                                  className={`ap-row${r.id === current?.id ? " on" : ""}${
                                    r.state === "aside" || r.state === "withdrawn" ? " faint" : ""}`}
                                  aria-current={r.id === current?.id ? "true" : undefined}
                                  onClick={() => setParam("a", r.id)}>
                            <span className="ap-who">
                              <b>{r.full_name || r.applicant.display_name}</b>
                              <span className="ap-mail">{r.applicant.email}</span>
                            </span>
                            <span className="ap-meta">
                              {r.comment_count > 0 && (
                                <span className="ap-c"
                                      title={`${r.comment_count} teacher comment${r.comment_count === 1 ? "" : "s"}`}>
                                  <Bubble />{r.comment_count}
                                  <span className="sr"> comments</span>
                                </span>)}
                              <span className="ap-date">
                                {r.loaded_at ? "loaded " : ""}{shortDay(r.loaded_at ?? r.submitted_at)}
                              </span>
                              <Badge tone={BADGE[r.state].tone}>{BADGE[r.state].word}</Badge>
                            </span>
                          </button>
                        </li>))}
                    </ol>)}
              </div>
              {current && (
                <ApplicantPanel key={current.id} row={current} questions={questions}
                                courseId={courseId} canEdit={canEdit}
                                position={shown.findIndex((r) => r.id === current.id)}
                                total={shown.length}
                                onGo={(by) => {
                                  const i = shown.findIndex((r) => r.id === current.id);
                                  const next = shown[i + by];
                                  if (next) setParam("a", next.id);
                                }}
                                onClose={() => setParam("a", "")}
                                onChanged={changed} />)}
            </div>)}
      </Pane>
    </div>
  );
}

/** ⚠️ DRAWN, NOT AN EMOJI — an emoji renders in colour on most platforms, the one coloured
 *  thing in a monochrome row (the rule `Glyph` on the Teams page records). */
function Bubble() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.8A8 8 0 1 1 21 12Z" />
    </svg>
  );
}

function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * One applicant, opened on the right (owner, on the first mockup: *"a panel on the right when we
 * click on them, with the accept and so on, also the possibility of adding comments for other
 * teachers to see!"*).
 */
function ApplicantPanel({ row, questions, courseId, canEdit, position, total, onGo, onClose,
                          onChanged }: {
  row: TeacherApplicationRow; questions: IntakeQuestion[]; courseId: string;
  canEdit: boolean; position: number; total: number;
  onGo: (by: number) => void; onClose: () => void; onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const name = row.full_name || row.applicant.display_name;

  const act = async (go: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await go(); setDeciding(false); setNote(""); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };
  const decide = (decision: "" | "accepted" | "declined") =>
    act(() => decideApplication(courseId, row.id, decision, note));
  const aside = (excluded: boolean) => act(() => excludeApplication(courseId, row.id, excluded));
  const withdrawn = Boolean(row.withdrawn_at);
  const decided = row.decision !== "";

  return (
    <aside className="card ap-panel" aria-label={`Application from ${name}`}>
      <div className="ap-head">
        <button className="btn ghost sm ap-x" aria-label="Close the application"
                onClick={onClose}>×</button>
        <div className="ap-title">
          <h3>{name}</h3>
          <span className="ap-mail">{row.applicant.email}</span>
        </div>
        <span className="ap-nav">
          <button className="btn sm icon" aria-label="Previous applicant"
                  disabled={position <= 0} onClick={() => onGo(-1)}>‹</button>
          <span className="ap-pos">{position >= 0 ? `${position + 1} of ${total}` : DASH}</span>
          <button className="btn sm icon" aria-label="Next applicant"
                  disabled={position < 0 || position >= total - 1}
                  onClick={() => onGo(1)}>›</button>
        </span>
      </div>
      <div className="ap-badges">
        <Badge tone={BADGE[row.state].tone}>{BADGE[row.state].word}</Badge>
        {row.decision === "accepted" && !row.on_roster && !withdrawn && (
          <span className="tierchip"
                title="Invite this email in Classroom; it reads Enrolled the moment it appears on the roster.">
            not on the roster yet</span>)}
        {!row.applicant.signed_in && (
          <span className="tierchip"
                title="Loaded from a file by email. They see this application the first time they sign in with that address.">
            waiting for sign-in</span>)}
        {row.is_staff && <span className="tierchip">staff</span>}
        {row.teacher_edited_at && <span className="tierchip">edited by a teacher</span>}
        <span className="spacer" />
        <span className="small muted">
          {row.loaded_at ? "Loaded " : "Applied "}<When iso={row.loaded_at ?? row.submitted_at} />
        </span>
      </div>

      <div className="ap-dec">
        {withdrawn ? (
          <span className="muted">They withdrew this application, so there is nothing to
            decide. It comes back if they apply again.</span>
        ) : decided && !deciding ? (<>
          <span className="ap-by">
            {row.decision === "accepted" ? "Accepted" : "Declined"} by {row.decided_by}
            {row.decided_at && <> · <When iso={row.decided_at} /></>}
            {row.decision_note && <span className="muted">: {row.decision_note}</span>}
          </span>
          <span className="spacer" />
          <button className="btn ghost sm" disabled={!canEdit || busy}
                  onClick={() => { setNote(row.decision_note); setDeciding(true); }}>
            Change decision</button>
        </>) : (<>
          <button className="btn primary" disabled={!canEdit || busy}
                  onClick={() => void decide("accepted")}>Accept</button>
          <button className="btn" disabled={!canEdit || busy}
                  onClick={() => void decide("declined")}>Decline</button>
          {decided && (
            <button className="btn ghost sm" disabled={!canEdit || busy}
                    onClick={() => void decide("")}>Leave undecided</button>)}
          <input className="input grow ap-note" value={note} maxLength={500}
                 disabled={!canEdit || busy}
                 placeholder="A note for the other teachers (optional)"
                 aria-label="A note on this decision, for the other teachers"
                 onChange={(e) => setNote(e.target.value)} />
          {deciding && (
            <button className="btn ghost sm" disabled={busy}
                    onClick={() => setDeciding(false)}>Cancel</button>)}
        </>)}
      </div>
      <div className="ap-acts">
        <span className="small muted">
          {withdrawn ? "" : "The applicant is not told a decision, and nobody is invited to Classroom from here."}
        </span>
        <span className="spacer" />
        {!withdrawn && (
          <button className="btn ghost sm" disabled={!canEdit || busy}
                  onClick={() => void aside(!row.excluded_at)}>
            {row.excluded_at ? "Put back" : "Set aside"}</button>)}
        <button className="btn ghost sm" disabled={!canEdit || busy}
                aria-pressed={editing} onClick={() => setEditing(!editing)}>
          {editing ? "Close the editor" : "Edit answers"}</button>
      </div>
      {err && <p className="alert refused ap-err" role="alert">⛔ {err.message}</p>}

      <div className="ap-scroll">
        <section className="ap-sec">
          <h4>Answers</h4>
          {editing ? (
            <AnswersEditor courseId={courseId} row={row} questions={questions}
                           onClose={() => setEditing(false)}
                           onSaved={() => { setEditing(false); onChanged(); }} />
          ) : <Answers row={row} questions={questions} />}
        </section>
        <Thread courseId={courseId} applicationId={row.id} canEdit={canEdit}
                count={row.comment_count} onChanged={onChanged} />
      </div>
    </aside>
  );
}

/** Their answers, in the form's own order. */
function Answers({ row, questions }: { row: TeacherApplicationRow; questions: IntakeQuestion[] }) {
  return (
    <div className="acard-body ap-answers">
      {questions.map((q) => {
        const v = row.answers[q.id];
        let shown: React.ReactNode = <span className="muted">{DASH}</span>;
        if (q.kind === "matrix" && v && typeof v === "object") {
          const levels = q.levels ?? [];
          shown = (
            <span className="aval ap-grid">
              {(q.rows ?? []).map((r) => {
                const at = levels.indexOf(v[r] ?? "");
                return (
                  <span className="ap-grid-row" key={r}>
                    <span>{r}</span>
                    <span className="ap-lvl" aria-hidden="true">
                      {levels.slice(1).map((l, i) => (
                        <i key={l} className={i < at ? "on" : ""} />))}
                    </span>
                    <span className="muted">{v[r] || DASH}</span>
                  </span>);
              })}
            </span>);
        } else if (q.kind === "scale" && typeof v === "number") {
          shown = <span className="aval">{v} <span className="muted">of {q.highest ?? 5}</span></span>;
        } else if (q.kind === "link" && typeof v === "string" && v) {
          // 🔴 ONLY A WEB ADDRESS IS A LINK. The applicant typed it, and a `javascript:` value
          // must not become something a teacher clicks.
          shown = /^https?:\/\//i.test(v)
            ? <a className="aval brk" href={v} target="_blank" rel="noreferrer noopener">{v}</a>
            : <span className="aval brk">{v}</span>;
        } else if (typeof v === "string" && v) {
          shown = <span className={q.kind === "timezone" || q.role === "discord_user_id"
            ? "aval mono" : "aval brk"}>{v}</span>;
        }
        return (
          <div className="arow" key={q.id}>
            <span className="alabel">{q.label}</span>
            {shown}
          </div>);
      })}
    </div>
  );
}

/**
 * One applicant's answers, open for a teacher to change (owner, 2026-09-22).
 *
 * 🔴 THE APPLICANT'S OWN CONTROLS and only the answers to this form's questions go back
 * (`answersFor`). A teacher is not bound by the form being shut; the server decides again.
 */
function AnswersEditor({ courseId, row, questions, onClose, onSaved }: {
  courseId: string; row: TeacherApplicationRow; questions: IntakeQuestion[];
  onClose: () => void; onSaved: () => void;
}) {
  const [draft, setDraft] = useState<ApplicationAnswers>(() => ({ ...row.answers }));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await editApplicationAnswers(courseId, row.id, answersFor(questions, draft));
      onSaved();
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <ApplicationQuestions questions={questions} draft={draft} locked={() => busy} note=""
                            set={(id, v) => { setDraft((x) => ({ ...x, [id]: v })); setDirty(true); }} />
      {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
      <div className="row gap">
        <button className="btn primary" disabled={busy || !dirty}
                onClick={() => void save()}>Save answers</button>
        <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * Comments for the other teachers.
 *
 * 🔴 TEACHERS ONLY, AND THE PAGE SAYS SO UNDER THE BOX. A removed comment keeps its place and
 * says who removed it, so a conversation never changes silently under the people in it.
 */
function Thread({ courseId, applicationId, canEdit, count, onChanged }: {
  courseId: string; applicationId: string; canEdit: boolean; count: number;
  onChanged: () => void;
}) {
  const q = useAsync(() => getApplicationComments(courseId, applicationId),
                     [courseId, applicationId, count]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const run = async (go: () => Promise<unknown>, clear = false) => {
    setBusy(true); setErr(null);
    try { await go(); if (clear) setText(""); q.reload(); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };
  useEffect(() => { setText(""); }, [applicationId]);
  const comments = q.data?.comments ?? [];
  return (
    <section className="ap-sec">
      <h4>Comments <span className="muted">· teachers only</span></h4>
      {comments.length === 0 && !q.loading
        ? <p className="small muted">No comments yet.</p>
        : (
          <ol className="ap-cms">
            {comments.map((c) => (
              <li className="ap-cm" key={c.id}>
                <div className="ap-cm-h">
                  <b>{c.author}</b><span className="muted"><When iso={c.created_at} /></span>
                  {c.mine && !c.removed && (<>
                    <span className="spacer" />
                    <button className="btn ghost sm" disabled={!canEdit || busy}
                            aria-label={`Remove your comment from ${shortDay(c.created_at)}`}
                            onClick={() => void run(() => removeApplicationComment(
                              courseId, applicationId, c.id))}>Remove</button>
                  </>)}
                </div>
                {c.removed
                  ? <p className="muted">Removed by {c.author}.</p>
                  : <p className="ap-cm-body">{c.body}</p>}
              </li>))}
          </ol>)}
      <form className="ap-compose" onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) void run(() => postApplicationComment(courseId, applicationId, text), true);
      }}>
        <label className="sr" htmlFor={`cm-${applicationId}`}>
          Write a comment for the other teachers</label>
        <textarea className="ta prose" id={`cm-${applicationId}`} rows={2} value={text}
                  maxLength={2000} disabled={!canEdit || busy}
                  placeholder="Write a comment for the other teachers"
                  onChange={(e) => setText(e.target.value)} />
        {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
        <div className="row gap">
          <span className="small muted">The applicant never sees comments.</span>
          <span className="spacer" />
          <button className="btn sm primary" type="submit"
                  disabled={!canEdit || busy || !text.trim()}>Comment</button>
        </div>
      </form>
    </section>
  );
}

/**
 * What loading a file would do, row by row, before anything is written.
 *
 * 🔴 MATCHED BY EMAIL AND NOTHING ELSE, and the preview says so: a row for somebody who already
 * applied is skipped and their own answers are kept; a row with no email is not loaded.
 */
function ImportPreview({ name, plan, busy, onCancel, onLoad }: {
  name: string; plan: ApplicationImport; busy: boolean; onCancel: () => void;
  onLoad: () => void;
}) {
  const c = plan.counts;
  const MAX = 50;
  const WORD: Record<ApplicationImport["steps"][number]["outcome"], string> = {
    new: "new", attach: "attach", skipped: "skipped", not_loaded: "not loaded",
  };
  const what = (s: ApplicationImport["steps"][number]) =>
    s.outcome === "new" ? "a new person, waiting for their first sign-in"
      : s.outcome === "attach"
        ? (s.signed_in ? "attached to their account" : "attached to a person waiting for sign-in")
        : s.outcome === "skipped"
          ? (s.applied_at ? `applied on ${shortDay(s.applied_at)}; their answers are kept` : s.why)
          : s.why;
  return (
    <div className="alert stack" role="status">
      <div className="imp-head"><strong>{name}</strong>
        <span className="muted"> · {plan.rows} row{plan.rows === 1 ? "" : "s"} · matched by
          email</span></div>
      <div className="imp-sum">
        <Badge tone="ok">{c.new} new</Badge>
        <span>a person is created by email; they see their application the first time they
          sign in with that address</span>
        <Badge tone="ok">{c.attach} existing</Badge>
        <span>somebody with that email already exists here; the application is theirs</span>
        <Badge tone="plain">{c.skipped} skipped</Badge>
        <span>already applied here, or the same email twice; nothing of theirs is changed</span>
        <Badge tone="stop">{c.not_loaded} not loaded</Badge>
        <span>no email, or an answer this form does not accept</span>
      </div>
      {plan.unmatched.length > 0 && (
        <p className="small"><strong>Ignored columns:</strong> {plan.unmatched.join(", ")} —
          this form asks nothing called that.</p>)}
      {plan.unanswered.length > 0 && (
        <p className="small"><strong>Nothing in the file for:</strong>{" "}
          {plan.unanswered.join(", ")} — those answers stay empty until the applicant gives
          them.</p>)}
      <div className="tablewrap">
        <table className="tb imp-tb">
          <thead><tr><th>Row</th><th>Email</th><th>Name</th><th>What happens</th></tr></thead>
          <tbody>
            {plan.steps.slice(0, MAX).map((s) => (
              <tr key={s.line}>
                <td className="num">{s.line}</td>
                <td className={s.email ? "mono" : "muted"}>{s.email || "no email"}</td>
                <td>{s.name || DASH}</td>
                <td>
                  <Badge tone={s.outcome === "not_loaded" ? "stop"
                    : s.outcome === "skipped" ? "plain" : "ok"}>{WORD[s.outcome]}</Badge>
                  {" "}{what(s)}
                  {s.dropped.length > 0 && (
                    <span className="muted"> · {s.dropped.join(", ")} left out: not every row
                      has a level</span>)}
                </td>
              </tr>))}
            {plan.steps.length > MAX && (
              <tr><td className="muted" colSpan={4}>… {plan.steps.length - MAX} more rows</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="row gap">
        <span className="spacer" />
        <button className="btn ghost" disabled={busy} onClick={onCancel}>Cancel</button>
        <button className="btn primary" disabled={busy || plan.would_load === 0} onClick={onLoad}>
          {busy ? "Loading…"
            : `Load ${plan.would_load} application${plan.would_load === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
